import { Button, Modal } from 'antd';
import React, { Component, ReactNode } from 'react';
import cx from 'classnames';
import Icon from '@ant-design/icons/lib/components/Icon';
import { connect } from 'react-redux';

import { toMonthDayYearFormat } from '../../accounts/account/utils';
import { CadenceTypesValues, pricePerCadence } from '../../auth/proRegister/subscriptionTypes';
import { ReactComponent as CrossSVG } from '../../../assets/sightx-icons/svg/x-close.svg';
import { IRemoveSeats, removeSeatsForPlgExternal2 } from '../../../slices/customer/customer.thunk';
import { RootState } from '../../../slices/rootReducer';
import { selectSubscriptionUpdatingState } from '../../../slices/customer/customer.selectors';

import styles from './removeSeats.module.scss';

interface IExternalProps {
  visible: boolean;
  usersToRemove: string[];
  seatsPurchased: number;
  seatsToRemove: number;
  onCancel: () => void;
  cycleEndDate: number;
  cadence: CadenceTypesValues;
  accountId: string;
}

interface IDispatchProps {
  removeSeats: (data: IRemoveSeats) => void;
}

interface IMapStateProps {
  loadingSubscription: boolean;
}

type Props = IExternalProps & IDispatchProps & IMapStateProps;

class ConfirmRemovalModalBase extends Component<Props> {

  constructor(props: Props) {
    super(props);
    this.handleConfirmation = this.handleConfirmation.bind(this);
  }

  private handleConfirmation() {
    const { accountId, seatsToRemove, usersToRemove, removeSeats } = this.props;
    removeSeats({
      customerId: accountId,
      seatsToRemoveQty: seatsToRemove,
      usersToRemove
    });
  }

  private renderTable() {
    const { seatsPurchased, seatsToRemove, cadence } = this.props;
    const pricePerSeat = pricePerCadence[cadence];
    const seatsToKeep = seatsPurchased - seatsToRemove;

    return <div className={styles.pricesTable}>
      <div className={styles.priceRow}>
        <div>
          Current subscription ({seatsPurchased} seats x ${pricePerSeat})
        </div>
        <div className={styles.value}>
          ${pricePerSeat * seatsPurchased}
        </div>
      </div>
      <div className={styles.priceRow}>
        <div>
          <b>{seatsToRemove}</b> {seatsToRemove > 1 ? 'seats' : 'seat'} removed
        </div>
        <div className={cx(styles.value, styles.negative)}>
          -${pricePerSeat * seatsToRemove}
        </div>
      </div>
      <div className={cx(styles.priceRow, styles.withDivisor)}>
        <div>
          <b>
            New subscription fee
          </b>
        </div>
        <div>
          <b>
            ${pricePerSeat * seatsToKeep}
          </b>
        </div>
      </div>
    </div>;
  }

  render(): ReactNode {
    const { visible, onCancel, seatsPurchased, seatsToRemove, cycleEndDate, loadingSubscription } = this.props;
    const seatsToKeep = seatsPurchased - seatsToRemove;
    const parsedDate = toMonthDayYearFormat(new Date(cycleEndDate * 1000));
    return <Modal
      visible={visible}
      onCancel={onCancel}
      title="Update Subscription"
      footer={null}
      className={styles.modal}
      width={600}
      closeIcon={<Icon className={styles.closeIcon} component={CrossSVG} />}
      maskClosable={false}
    >
      <div className={styles.content}>
        <div className={styles.summary}>
          <div className={styles.subtitle}>
            Subscription summary
          </div>
          <div>
            Your subscription will be updated from <b>{seatsPurchased}</b> seats to <b>{seatsToKeep}</b> seats when your next billing cycle begins, on <b>{parsedDate}.</b>
          </div>
          {this.renderTable()}
        </div>
        <div className={cx(styles.row, styles.actions)}>
          <Button
            loading={loadingSubscription}
            className={styles.button}
            type='ghost'
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            loading={loadingSubscription}
            className={styles.button}
            type='primary'
            onClick={this.handleConfirmation}
          >
            Update subscription
          </Button>
        </div>
      </div>
    </Modal>;
  }
}

const mapStateToProps = (state: RootState): IMapStateProps => {
  return {
    loadingSubscription: selectSubscriptionUpdatingState(state)
  };
};

const dispatchProps: IDispatchProps = {
  removeSeats: removeSeatsForPlgExternal2
};

export const ConfirmRemovalModal = connect(mapStateToProps, dispatchProps)(ConfirmRemovalModalBase);